import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom } from 'jotai';
import Link from 'next/link';

import { FundingTypeEnum } from '@flipster/apis/v2/snapshot';
import { FundingTypeTextMapper } from '@flipster/apis/v2/snapshot/constants';
import { SymbolMaxLeverageBadge } from '@flipster/features/src/misc/badge';
import { SymbolDisplay } from '@flipster/features/src/misc/display';
import { OrderAttributionMapper, orderAttributionAtom } from '@flipster/features/src/shared/order-attribution';
import { isTradFiInstrument } from '@flipster/features/src/shared/rwa';
import { useSnapshotSpec } from '@flipster/features/src/shared/snapshot/use-snapshot-spec';
import { FormattedMessage } from '@flipster/i18n';
import { Market, marketsBunja } from '@flipster/r230522/original/market';
import { getTickerDisplayPrice } from '@flipster/r230522/original/ticker-price';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { TextButton } from '@flipster/react/design-system/text-button';
import Formatted from '@flipster/react/design-utils/formatted';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, linkOverlay, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { FundingRatePrecision } from '@flipster/utils/constants/currency';
import segment from '@flipster/utils/segment';
import { MainEvent } from '@flipster/utils/segment/events/main';

import { CarouselMapper, MarketCarouselType } from './constants';
import { SortedSymbolsCategory } from './types';
import { usePerpTopMarkets } from './use-perp-top-markets';

interface Props {
  carouselId: MarketCarouselType;
  onClickSeeAll?: (tab: SortedSymbolsCategory) => void;
}

export const PerpTopMarket = ({ carouselId, onClickSeeAll }: Props) => {
  const { sortedTopMarkets } = usePerpTopMarkets();

  const { title, sub, alertProps, seeAll, category } = CarouselMapper[carouselId];

  // if no items, return null
  if (!sortedTopMarkets[carouselId]?.length) return null;

  return (
    <section
      className={css({
        minW: { base: 310, tablet: 360 },
        minH: 270,
        py: { base: 12, tablet: 20 },
        px: { base: 4, tablet: 10 },
        rounded: 12,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'border.default.level-1',
      })}
    >
      <header className={hstack({ justify: 'space-between', h: 26, px: 12, mb: 12 })}>
        <h4 className={css({ textStyle: 'Title/XS', color: 'content.default.level-5', flex: 1 })}>
          {title}
          {sub && <span className={css({ textStyle: 'Text/M/Regular', ml: 4 })}>{sub}</span>}
          {alertProps && (
            <button
              type='button'
              onClick={() =>
                dialog.open({
                  title: { label: alertProps.title },
                  content: alertProps.message,
                  buttons: {
                    primary: <Dialog.Button hierarchy='primary' label={alertProps.confirmText} />,
                  },
                })
              }
              className={css({ buttonShape: 'inline-icon', ml: 2 })}
            >
              <InformationCircleIcon size={20} color={token('colors.content.default.level-1')} />
            </button>
          )}
        </h4>
        {seeAll && onClickSeeAll && (
          <TextButton
            color='passive'
            size='medium'
            label={<FormattedMessage id='Common.r250919.See_all' />}
            onClick={() => onClickSeeAll(category!)}
          />
        )}
      </header>
      <ul>
        {sortedTopMarkets[carouselId]?.map((symbol, index) => {
          return <Item symbol={symbol} carouselId={carouselId} index={index} key={`${title}_${symbol}`} />;
        })}
      </ul>
    </section>
  );
};

interface ItemProps {
  symbol: string;
  carouselId: MarketCarouselType;
  index: number;
}
const Item = ({ symbol, carouselId, index }: ItemProps) => {
  const { marketsAtom } = useBunja(marketsBunja);
  const market = useAtomValue(marketsAtom);
  const setOrderAttribution = useSetAtom(orderAttributionAtom);

  const { instrumentsKeyBySymbol } = useSnapshotSpec();

  const currentInstrument = instrumentsKeyBySymbol?.[symbol];
  const currentMarket: Market | undefined = market?.[symbol];
  const displayPrice = getTickerDisplayPrice(currentMarket);

  return (
    <li
      className={css({
        pos: 'relative',
        p: 10,
        rounded: 8,
        _hover: {
          bgColor: 'background.default.level-3',
        },
      })}
    >
      <div className={hstack({ gap: 8 })}>
        <div className={css({ flex: 1, minW: 0 })}>
          <SymbolDisplay
            symbol={symbol}
            css={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)' }}
            icon={<SymbolDisplay.Icon size={32} space={12} />}
            mainLabel={
              <div className={flex({ gap: 4, minW: 0, alignItems: 'center' })}>
                <SymbolDisplay.Label
                  value='shortName'
                  css={{ textStyle: 'Body/L/Strong', color: 'inverse.white', whiteSpace: 'nowrap', flexShrink: 0 }}
                />
                {currentInstrument && (
                  <SymbolMaxLeverageBadge
                    crossMax={currentInstrument.maxLeverageHardLimit}
                    isolatedCap={currentInstrument.maxInitLeverageIsolatedMargin}
                  />
                )}
              </div>
            }
            subLabel={
              currentInstrument && isTradFiInstrument(currentInstrument) ? (
                <span
                  className={css({
                    display: 'block',
                    textStyle: 'Caption/L/Monospace/Regular',
                    color: 'content.accent.promotion',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  })}
                >
                  {currentInstrument.displayName}
                </span>
              ) : undefined
            }
          />
        </div>
        <div
          className={stack({
            align: 'flex-end',
            gap: 2,
            flexShrink: 0,
            textStyle: 'Body/L/Monospace/Strong',
            color: 'content.default.level-5',
          })}
        >
          {carouselId === 'volume' && (
            <>
              <Formatted.Number
                method='round-half-up'
                value={displayPrice}
                precision={currentInstrument?.pricePrecision}
                toFixed
              />
              <Formatted.ShortNumber
                value={currentMarket?.turnover24h}
                toFixed
                css={{ textStyle: 'Body/S/Monospace/Regular', color: 'content.default.level-2' }}
              />
            </>
          )}
          {carouselId === 'absFundingRates' && (
            <>
              <Formatted.Percent
                value={currentMarket?.fundingRate}
                precision={FundingRatePrecision}
                isRate
                toFixed
                withColor
                withPlus
              />
              <span
                className={css({ textStyle: 'Body/S/Monospace/Regular', color: 'content.default.level-2' })}
              >{`/${FundingTypeTextMapper[currentInstrument?.fundingType ?? FundingTypeEnum.ImmediateHour8]}`}</span>
            </>
          )}
          {carouselId !== 'volume' && carouselId !== 'absFundingRates' && (
            <>
              <Formatted.Percent value={currentMarket?.priceChangePct24h} precision={2} toFixed withColor withArrow />
              <Formatted.Number
                method='round-half-up'
                value={displayPrice}
                precision={currentInstrument?.pricePrecision}
                css={{ textStyle: 'Body/S/Monospace/Regular' }}
                toFixed
              />
            </>
          )}
        </div>
      </div>

      {currentInstrument && (
        <Link
          href={`/trade/perpetual/${symbol}`}
          onClick={() => {
            setOrderAttribution(OrderAttributionMapper.market.categories[carouselId]);
            segment.track(MainEvent.CarouselProductClick, {
              carousel_type: carouselId,
              symbol,
              list_order: index,
              current_price: displayPrice || '0',
              percentage_diff: currentMarket?.priceChangePct24h || '0',
            });
          }}
          className={linkOverlay()}
        />
      )}
    </li>
  );
};
